<template lang="pug">
#room-list(v-cloak)
  .box
    .box-header.d-flex.flex-row
      .titles
        .title
          span 部屋情報
        .note
          span 利用者登録時に選択する部屋をご登録ください（部屋が無い場合は仮の部屋を１つ作成し、全利用者でご使用ください）。
          br
          span ベーシックプランでは空床管理／情報提供機能を使用できます（空床管理対象外を除く）
          .error-message(v-if="errorMessage") {{ errorMessage }}
      .flex-fill.d-flex.flex-row-reverse
        button.btn.add-btn(v-if="_showControlButton" @click="gotoRoomRegister()" type="button") 部屋追加
        button.btn.multi-add-btn.mr-2(v-if="_showControlButton" @click="gotoRoomMultipleRegister()" type="button") 部屋一括登録
    .box-body
      RoomTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        @onEdit="gotoRoomEdit"
        @onDelete="showRoomDeleteModal"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        // 検索
        .search-filter.d-flex.mr-1.align-items-center.mr-2
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・部屋名
        // フィルタリング
        span.table-option-text 利用者グループ:
        .filter
          b-form-select(
            id="filter-input"
            v-model="filter.residentGroupId"
            :options="filterOptions"
            size="md"
          )
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        // ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )

  OkCancelModal(v-model="showDeleteRoomModal" :title="deleteRoomMessage" @ok="deleteRoom")
  OkModal(v-model="showDeleteRoomSuccessModal" title="削除に成功しました。" @ok="createView")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore, itemListFilterStore, needRefreshesStore } from "@/store/store";
import { SendWaiterMessagesState } from "@/store/NeedRefreshes";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import RoomsService, { RoomGetModel } from "@/commons/services/Rooms";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import RoomTable, { FilterSetting, RoomTableField, RoomTableItem } from "@/pc/containers/settings/RoomTable.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * 部屋一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    HelpIcon,
    RoomTable
  }
})
export default class RoomList extends Vue {
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 削除確認モーダル表示可否 */
  showDeleteRoomModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteRoomSuccessModal: boolean = false;

  /** テーブル列名 */
  fields: Array<RoomTableField> = [];
  /** テーブル各行の情報 */
  items: Array<RoomTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** グループIDとグループ名のMAP */
  groupNameMap: Map<number, string> = new Map();
  /** 削除対象の部屋ID */
  deleteTargetRoomId: number | null = null;
  /** 削除モーダルに表示するメッセージ */
  deleteRoomMessage: string = "";

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", residentGroupId: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.room.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.room.pageOptions;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  get _showControlButton(): boolean {
    return userStore.user.permissions.functions.roomsFunction;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `room-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<RoomTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.room.perPage);
    // フィルタの復元
    this.restoreFilterSetting();

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<RoomTableField> {
    let fields: Array<RoomTableField> = [];

    fields.push({ key: "residentGroupName", label: "利用者グループ", sortable: false, sortDirection: "desc", class: "resident-group-name" });
    fields.push({ key: "name", label: "部屋名", sortable: false, sortDirection: "desc", class: "room-name" });
    fields.push({ key: "useType", label: "部屋用途", sortable: false, sortDirection: "desc", class: "use-type" });
    fields.push({ key: "roomType", label: "部屋種別", sortable: false, sortDirection: "desc", class: "room-type" });
    fields.push({ key: "capacity", label: "部屋定員", sortable: false, sortDirection: "desc", class: "capacity" });
    if (this._showControlButton) {
      fields.push({ key: "operation", label: "登録情報", sortable: false, sortDirection: "desc", class: "operation text-center" });
    }

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["room-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupNameMap: Map<number, string> = this.createGroupNameMap(groups);
    this.groupNameMap = groupNameMap;

    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;

    // テーブルに渡す各プロパティを作成する
    const rooms: Array<RoomGetModel> = await this.getRooms();
    const items: Array<RoomTableItem> = this.createItems(rooms, this.groupNameMap);
    this.items = this.items.concat(items);
    this.currentRows = this.items.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.roomsService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### グループIDとグループ名のMAPを作成する
   */
  public createGroupNameMap(residentGroups: Array<ResidentGroupsGetModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();
    for (const group of residentGroups) {
      map.set(group.id, group.name);
    }

    return map;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    let filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /**
   * ### 部屋を取得する
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    let response: Array<RoomGetModel> = [];

    try {
      response = await this.roomsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.roomsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.roomsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(rooms: Array<RoomGetModel>, groupNameMap: Map<number, string>): Array<RoomTableItem> {
    let items: Array<RoomTableItem> = [];

    for (const room of rooms) {
      const groupName: string | undefined = groupNameMap.get(room.residentGroupId);
      items.push({
        id: room.id,
        name: room.name,
        useType: room.useType,
        roomType: room.roomType,
        capacity: room.capacity,
        residentGroupId: room.residentGroupId,
        residentGroupName: groupName ? groupName : "",
        updated: room.updated
      });
    }

    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 部屋一括登録画面へ遷移する
   */
  public gotoRoomMultipleRegister(): void {
    this.$router.push(PcUrl.RoomMultipleRegister);
    return;
  }

  /**
   * ### 部屋登録画面へ遷移する
   */
  public gotoRoomRegister(): void {
    this.$router.push(PcUrl.RoomRegister);
    return;
  }

  /**
   * ### 部屋編集画面へ遷移
   */
  public gotoRoomEdit(room: RoomTableItem): void {
    this.$router.push({ name: PcUrl.RoomEdit, params: { id: `${room.id}` } });
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   */
  public showRoomDeleteModal(room: RoomTableItem): void {
    this.deleteTargetRoomId = room.id;
    const roomName: string = room.name ? room.name : "";
    this.deleteRoomMessage = `${roomName}を削除しますか？`;
    this.showDeleteRoomModal = true;

    return;
  }

  /**
   * ### 部屋を削除する
   *
   * - 部屋削除をサービス実行
   * - 削除成功ダイアログを表示する
   * - ページに表示する部屋を取得しなおす
   */
  public async deleteRoom(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    try {
      await this.roomsService.delete(userStore, this.deleteTargetRoomId as number);
      this.showDeleteRoomSuccessModal = true;

      // メッセージ送信(待機者)のテンプレートが変更になるので再読み込みさせるようstoreを更新
      let sendWaiterMessageState: SendWaiterMessagesState = needRefreshesStore.sendWaiterMessage;
      sendWaiterMessageState.sendMessages = true;
      needRefreshesStore.setSendWaiterMessage(sendWaiterMessageState);

      // 削除による画面の再描画
      this.items = [];
      this.currentRows = this.items.length;
      this.currentPage = 1;

      await this.createView();
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      if (systemError.statusCode == 409) {
        this.errorMessage = "利用者が存在するため、部屋を削除できませんでした。利用者の部屋を変更して再度削除してください";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#room-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    width: 100%;

    .title {
      font-size: 22px;
      font-weight: bold;
    }
    .multi-add-btn {
      width: 130px;
      min-width: 130px;
    }
    .add-btn {
      width: 130px;
      min-width: 130px;
    }
  }
}
</style>
